/**
 * Types for tenant-facing `/v1/billing/*` endpoints on platform-api (R1 scope).
 *
 * Storage uses micro-USD as the authoritative billing unit. Tenant-facing
 * responses expose `*UsdMicros` for exact values, retain integer-cents fields
 * (`*UsdCents`) as compatibility projections, and include a decimal-string
 * rendering (`*Usd`, e.g. `"5.00"`) so clients can display values without
 * re-introducing IEEE-754 drift at the JSON boundary.
 *
 * Ledger sign convention (see ADR-003 + /internal/billing/charge):
 *   - Debits (charges) are *negative* integer micro-USD.
 *   - Credits (gift-card redemptions, top-ups) are *positive* integer micro-USD.
 *   - SUM(amountUsdMicros) for an account == `account_balances.balance_usd_micros`.
 */

import type { BillingStatementStatus } from './internal-billing-statements.ts';
import type { UnitUsageUnit } from './metering.ts';
import type { PromoterGrantStatus } from './promoter.ts';
import type { PromoterBindingView } from './public-promoter.ts';

/**
 * Narrow cashier-channel alias shared between `/v1/billing/topup` request and
 * ZPay's provider adapter. The string literal union is stable across phases;
 * future providers must extend this type at the contracts boundary rather
 * than widening their local call-site.
 */
export type PayType = 'alipay' | 'wxpay';

export interface BalanceResponseBody {
  accountId: string;
  orgId: string;
  /** Integer micro-USD. Source of truth. */
  balanceUsdMicros: number;
  /** Integer USD cents compatibility projection, floored from balanceUsdMicros. */
  balanceUsdCents: number;
  /** Decimal-string rendering of balanceUsdMicros for display, e.g. "5.00". */
  balanceUsd: string;
  updatedAt: string;

  /**
   * How much of the balance is promotional (gift) credit, and how much is
   * cash-equivalent. Derived from the outstanding `balance_credit_lots`
   * remainders, not from a separate counter.
   *
   * Every field below is OPTIONAL on the wire: a client older than the
   * platform that introduced them, and a client newer than a platform that
   * has not shipped them yet, must both render the balance without the
   * split rather than showing a zero. Deploy order is platform-before-
   * console, so "absent" is the transient a console will actually meet.
   *
   * Invariants a renderer may rely on when the fields ARE present:
   *   - `promoUsdMicros + cashUsdMicros === max(balanceUsdMicros, 0)`
   *   - both are >= 0; a credit customer in overdraft
   *     (`balanceUsdMicros < 0`) reports 0 / 0, because there is no
   *     positive balance left to attribute to a source
   *   - the split is clamped to the balance. The per-source ledger is kept
   *     equal to the balance by `balance-source-reconciliation`, but charge
   *     allocation is best-effort, so a drifted account would otherwise
   *     report a gift total larger than the money it has. Under-reporting
   *     the gift share is the safe direction; over-reporting it is not.
   */
  promoUsdMicros?: number;
  promoUsdCents?: number;
  promoUsd?: string;
  cashUsdMicros?: number;
  cashUsdCents?: number;
  cashUsd?: string;
  /**
   * When the soonest-expiring outstanding promotional lot expires, and how
   * much of it is left — NOT the whole gift balance. "¥200 expires in 7
   * days" must name the batch that actually expires then; a tenant holding
   * two gifts with different expiries would otherwise read the earliest
   * date against the largest amount and believe all of it is about to
   * vanish. `null` when no outstanding promotional lot has an expiry.
   */
  promoNextExpiresAt?: string | null;
  promoNextExpiringUsdMicros?: number;
  promoNextExpiringUsdCents?: number;
  promoNextExpiringUsd?: string;

  /**
   * This customer's credit line, nested so a client that does not parse it
   * still renders the balance. OPTIONAL on the wire for the same deploy
   * reason as the gift/cash split: a client older than the platform that
   * introduced the block, and a client newer than a platform that has not
   * shipped it yet, must both render the balance without a credit panel
   * rather than showing a zero limit.
   *
   * `{ granted: false }` is a truthy object. A renderer MUST inspect the
   * discriminant fields, never `if (body.credit)`:
   *   - absent: this platform cannot tell you
   *   - `{ granted: false }` with no `residual`: prepaid. Distinct from a
   *     zero remaining limit. Never carries platform defaults
   *     (`credit_settings.default_limit_*` / `default_grace_days`) —
   *     those are not this customer's terms (router#2061).
   *   - `{ granted: false, residual: true, ... }`: the line has ended
   *     (expired / superseded) but live debt remains. Not prepaid —
   *     额度结束 ≠ 债务消失. Stage, overdue days, unpaid amount and the
   *     open due date are on this object.
   *   - `{ granted: true, ... }`: an effective grant. A 0 remaining
   *     balance is a real 0, not "no line".
   */
  credit?: TenantCreditStatus;
}

/** Tenant-visible grant type. `temporary_bump` is not implemented and is not exposed. */
export const TENANT_CREDIT_KINDS = ['long_term', 'temporary'] as const;
export type TenantCreditKind = (typeof TENANT_CREDIT_KINDS)[number];

/**
 * Overdue-ladder position the tenant can act on. `normal` is the absence
 * of an episode (including a `cleared` row). Freeze and stop are
 * different stages on purpose: freeze does not block usage; stop does.
 */
export const TENANT_CREDIT_STAGES = [
  'normal',
  'frozen',
  'stop_notice',
  'stopped',
  'collections',
] as const;
export type TenantCreditStage = (typeof TENANT_CREDIT_STAGES)[number];

export function isTenantCreditKind(value: string): value is TenantCreditKind {
  for (const candidate of TENANT_CREDIT_KINDS) {
    if (candidate === value) return true;
  }
  return false;
}

export function isTenantCreditStage(value: string): value is TenantCreditStage {
  for (const candidate of TENANT_CREDIT_STAGES) {
    if (candidate === value) return true;
  }
  return false;
}

/** One effective grant the tenant holds, with the locked approval FX. */
export interface TenantCreditLine {
  limitUsdMicros: number;
  limitUsdCents: number;
  limitUsd: string;
  /** Operator CNY original locked at approval; null on pre-cutover grants. */
  limitCnyCents: number | null;
  /** USD_CNY rate used to lock `limitUsdMicros`; null on pre-cutover grants. */
  appliedRate: number | null;
  usedUsdMicros: number;
  usedUsdCents: number;
  usedUsd: string;
  remainingUsdMicros: number;
  remainingUsdCents: number;
  remainingUsd: string;
}

/** Temporary grant plus the dated window and the one-shot bill it produces. */
export interface TenantTemporaryCreditLine extends TenantCreditLine {
  /** UTC+8 calendar date 'YYYY-MM-DD' the grant became effective. */
  startsOn: string;
  /** UTC+8 calendar date 'YYYY-MM-DD' the floor ends (the 死期). */
  endsOn: string;
  /**
   * Temporary credit bills once when the grant ends, not on the monthly
   * cycle. Always true for an exposed temporary line — the frozen type
   * template is `one_shot_on_expiry`.
   */
  oneShotBillOnExpiry: true;
}

export interface TenantCreditExemption {
  /** Instant the exemption ends. */
  expiresAt: string;
  /** UTC+8 calendar date 'YYYY-MM-DD' of `expiresAt`. */
  expiresOn: string;
}

export interface TenantCreditNotGranted {
  granted: false;
}

/**
 * The line has ended and live debt remains. `granted` is false because
 * there is no effective floor; `residual: true` is the discriminant that
 * separates this from prepaid. A renderer that only checks `granted`
 * will hide the debt — that is the failure this state exists to prevent.
 */
export interface TenantCreditResidual {
  granted: false;
  residual: true;
  /**
   * Signed balance + the spendability projection the gateway reads
   * (`accounts.credit_limit_usd_micros`). With no effective grant that
   * column is 0, so this is the (usually negative) book position.
   */
  availableUsdMicros: number;
  availableUsdCents: number;
  availableUsd: string;
  /** Live unpaid amount: `max(0, -balance)`. */
  usedUsdMicros: number;
  usedUsdCents: number;
  usedUsd: string;
  /**
   * Gateway spendability column, COALESCE 0. Not the ended grant's
   * contract amount.
   */
  spendableLimitUsdMicros: number;
  billingGraceDays: number | null;
  stage: TenantCreditStage;
  newCreditFrozen: boolean;
  /**
   * True when the spendability projection is 0 while a contract floor
   * is still positive. Residual has no contract floor, so this is
   * false; generation is still refused when `availableUsdMicros` is
   * not positive — the same arithmetic the gateway uses.
   */
  serviceStopped: boolean;
  overdueDays: number;
  exemption: TenantCreditExemption | null;
  currentPeriodDueDate: string | null;
  /** True when an open one-shot statement (temporary-credit bill) exists. */
  oneShotBillOpen: boolean;
}

export interface TenantCreditGranted {
  granted: true;
  kinds: readonly TenantCreditKind[];
  longTerm: TenantCreditLine | null;
  temporary: TenantTemporaryCreditLine | null;
  /**
   * This account's own payment terms in days after period end. `null`
   * when the column is unset — never coalesced onto the platform
   * default (router#2061).
   */
  billingGraceDays: number | null;
  /** Sum of effective grant floors. Not affected by the D+7 overlay. */
  contractLimitUsdMicros: number;
  /**
   * `accounts.credit_limit_usd_micros` COALESCE 0 — the spendability
   * projection the gateway reads, including the D+7 overlay. This is
   * the number that actually moves `can_spend`, not the contract floor.
   */
  spendableLimitUsdMicros: number;
  /**
   * Signed balance + `spendableLimitUsdMicros`. Matches the credit
   * term of gateway `can_spend`. A stopped overlay zeros the
   * spendability column, so this is the book position, not
   * balance + contract floor.
   */
  availableUsdMicros: number;
  availableUsdCents: number;
  availableUsd: string;
  /** How much of the line is drawn: `max(0, -balance)`. */
  usedUsdMicros: number;
  usedUsdCents: number;
  usedUsd: string;
  /** Headroom left on the contract floor: `max(0, contractLimit - used)`. */
  remainingUsdMicros: number;
  remainingUsdCents: number;
  remainingUsd: string;
  /** Spend past both contract floors. 0 when inside the line. */
  overLimitUsdMicros: number;
  overLimitUsdCents: number;
  overLimitUsd: string;
  stage: TenantCreditStage;
  /**
   * True from freeze through collections. New grants/increases are
   * refused; usage is NOT blocked by this flag.
   */
  newCreditFrozen: boolean;
  /**
   * True when `spendableLimitUsdMicros === 0` while
   * `contractLimitUsdMicros > 0` — the D+7 overlay the gateway
   * consumes. This is not "any generation refusal": an exhausted
   * line (stage normal, available 0) still has a positive
   * spendability cap and is refused because available is 0.
   */
  serviceStopped: boolean;
  overdueDays: number;
  exemption: TenantCreditExemption | null;
  /** Earliest open statement due date, UTC+8 'YYYY-MM-DD'. Null if none. */
  currentPeriodDueDate: string | null;
  /**
   * Next open-statement due date after `currentPeriodDueDate`, or for
   * an active temporary grant the one-shot due (expiry + 7 calendar
   * days, `TEMPORARY_CREDIT_ONE_SHOT_DUE_DAYS`). Null when neither
   * exists. Not a synthesized next monthly cycle.
   */
  nextDueDate: string | null;
  /** True when an open one-shot statement exists. */
  oneShotBillOpen: boolean;
}

export type TenantCreditStatus =
  | TenantCreditNotGranted
  | TenantCreditResidual
  | TenantCreditGranted;

export function isTenantCreditResidual(
  value: TenantCreditStatus,
): value is TenantCreditResidual {
  return value.granted === false && 'residual' in value && value.residual === true;
}

/**
 * Tenant-visible classification of a credit lot. The storage-level
 * `BalanceCreditSourceType` is deliberately NOT exposed: `bootstrap_credit`,
 * `ops_adjust` and `unknown_legacy` describe internal bookkeeping, and a
 * tenant only has a decision to make about one distinction — will this
 * money expire on me or not.
 */
export type CreditLotKind = 'promo' | 'cash';

export interface CreditLotEntry {
  id: string;
  kind: CreditLotKind;
  /** Remaining spendable amount of this lot, integer micro-USD. */
  remainingUsdMicros: number;
  remainingUsdCents: number;
  remainingUsd: string;
  /** Original amount of this lot when it was credited. */
  amountUsdMicros: number;
  amountUsdCents: number;
  amountUsd: string;
  /** `null` for credit that never expires (cash always does not expire). */
  expiresAt: string | null;
  issuedAt: string;
}

export interface CreditsResponse {
  /**
   * Outstanding lots (remainder > 0) in the order they will be consumed:
   * expiring credit first, soonest expiry first. This is the same ordering
   * `promo_expiry_fifo_v1` uses to allocate a charge, so the list reads top
   * to bottom as "what gets spent next".
   *
   * Bounded to `CREDIT_LOTS_MAX_LIMIT`. Never carries `ownerOpsUserId` or
   * any other operator-side attribution.
   */
  lots: readonly CreditLotEntry[];
  /** The allocation rule the ordering reflects, so a client can label it. */
  allocationPolicy: 'promo_expiry_fifo_v1';
}

export const CREDIT_LOTS_DEFAULT_LIMIT = 50;
export const CREDIT_LOTS_MAX_LIMIT = 200;

export interface BalanceTransactionEntry {
  id: string;
  type: string;
  /** Signed integer micro-USD (negative = debit, positive = credit). */
  amountUsdMicros: number;
  /** Signed integer USD cents compatibility projection. */
  amountUsdCents: number;
  /** Decimal-string rendering of amountUsdMicros, e.g. "-1.25". */
  amountUsd: string;
  reason: string | null;
  actorType: string;
  actorUserId: string | null;
  externalRequestId: string | null;
  createdAt: string;
}

export interface TransactionsResponse {
  transactions: readonly BalanceTransactionEntry[];
}

/**
 * One per-unit price row on the tenant pricing surface (#1706). Integer
 * micro-USD per unit, matching `model_unit_pricing` — never a float rate.
 * `variantKey: ''` is the default row a missing variant falls back to.
 */
export interface PricingUnitRate {
  unit: UnitUsageUnit;
  variantKey: string;
  rateUsdMicros: number;
}

/**
 * The additive unit-price projection for unit-metered models (#1706): the
 * current `model_unit_pricing` generation, at BASE (undiscounted) rates —
 * the same base semantics as the six per-Mtok dimensions beside it. A
 * unit-metered model's token dimensions are the deliberately zero-rate
 * anchor generation (the ANCHOR CONTRACT), so a console must render these
 * rates instead of the zeros; this field's presence is the discriminator.
 *
 * Attached when every ENABLED metering profile of the (vendor, model) is
 * unit-metered; a model that also carries an enabled token profile keeps
 * its token dimensions and never gets this field. `rates` is EMPTY when
 * the model is unit-metered but no price generation is currently
 * effective: render "pricing pending", never zeros and never free.
 */
export interface PricingEntryUnitPricing {
  rates: readonly PricingUnitRate[];
}

export interface PricingEntry {
  vendor: string;
  model: string;
  /**
   * USD per million input tokens. Kept as a floating-point rate (not an
   * integer-cent amount) because it's a multiplicative coefficient in the
   * cost formula — converting to integer cents here would force a second
   * lossy rounding on the rate itself.
   */
  inputPerMtokUsd: number;
  outputPerMtokUsd: number;
  cacheReadPerMtokUsd: number;
  cacheWritePerMtokUsd: number;
  cacheWrite5mPerMtokUsd?: number | null;
  cacheWrite1hPerMtokUsd?: number | null;
  reasoningPerMtokUsd?: number | null;
  imageInputPerMtokUsd?: number | null;
  effectiveFrom: string;
  /** Present only for unit-metered models (#1706); token rows never carry it. */
  unitPricing?: PricingEntryUnitPricing;
}

export interface PricingResponse {
  pricing: readonly PricingEntry[];
  /** Deprecated scalar org discount field; channel-specific rates live in catalog. */
  orgDiscountMultiplier?: number;
}

export interface BillingDisplayRateResponse {
  pair: 'USD_CNY';
  /** Configured CNY per 1 USD from exchange_rate_policy. */
  cnyPerUsd: number;
  updatedAt: string;
  source: 'exchange_rate_policy';
}

export interface RedeemGiftCardRequest {
  code: string;
  /** Optional idempotency key; server generates one when omitted. */
  externalRequestId?: string;
}

export interface RedeemGiftCardResponse {
  kind: 'gift_card';
  code: string;
  /** Positive integer RMB cents displayed as the card face value. */
  amountCnyCents: number;
  /** Positive integer USD cents (credited amount). */
  amountUsdCents: number;
  /** Positive integer micro-USD (credited amount). */
  amountUsdMicros: number;
  /** Decimal-string rendering of amountUsdMicros, e.g. "25.00". */
  amountUsd: string;
  /** Locked CNY-per-USD policy rate used when the card was issued/backfilled. */
  appliedRate: number;
  transactionId: string;
  /** Integer USD cents (post-redemption balance). */
  newBalanceUsdCents: number;
  /** Integer micro-USD (post-redemption balance). */
  newBalanceUsdMicros: number;
  /** Decimal-string rendering of newBalanceUsdMicros, e.g. "75.50". */
  newBalanceUsd: string;
}

/**
 * Promoter-code arm of `POST /v1/billing/redeem` (A2c). Binding and the
 * grant land on the org's owner (billing) account, even when an admin
 * with `org:billing:manage` submits the code.
 */
export interface RedeemPromoterCodeResponse {
  kind: 'promoter_code';
  binding: PromoterBindingView;
  grant: {
    status: PromoterGrantStatus;
    amountUsdMicros?: number;
    expiresAt?: string;
    lotId?: string;
  };
}

export type RedeemResponse = RedeemGiftCardResponse | RedeemPromoterCodeResponse;

/**
 * One tenant-visible billing statement (epic #1033 E4-C9, #1133).
 *
 * The tenant-facing projection of a `billing_statements` row served by
 * `GET /v1/billing/statements` (session-account-scoped). It deliberately omits
 * every ops-internal field: `owner_ops_user_id` — the active manager stamped at
 * close (Epic 5 sales feed) — is NEVER exposed to tenants, and no
 * upstream/routing/procurement field appears here (tenants stay black-boxed
 * from routing per the router architecture rules).
 *
 * Money is integer micro-USD (authoritative `*UsdMicros`) with cents + decimal
 * projections for display, matching the rest of `/v1/billing/*`.
 * `cnyReferenceClosingArCents` is integer CNY cents at the issuance-time policy
 * rate — a reference figure only; AR is USD-denominated.
 *
 * `consumptionUsdMicros` is INFORMATIONAL gross period usage; it is NOT the AR
 * driver (closing AR is the reconstructed debt position at the cut, which
 * excludes prepaid-covered consumption). UIs must label it as informational
 * usage, not "amount owed".
 *
 * `status` is the settlement state carried by the row ('open' = AR still
 * outstanding, 'closed' = debt cleared or born with no debt), NOT a payment-due
 * flag. Clients derive "overdue" as `status === 'open' && dueDate < today` — the
 * row does not store an overdue flag.
 */
export interface V1BillingStatement {
  id: string;
  /** UTC+8 calendar date 'YYYY-MM-DD', inclusive period start. */
  periodStart: string;
  /** UTC+8 calendar date 'YYYY-MM-DD', exclusive period end. */
  periodEnd: string;
  /** INFORMATIONAL gross period usage, integer micro-USD (>= 0). */
  consumptionUsdMicros: number;
  consumptionUsdCents: number;
  consumptionUsd: string;
  /** Prior statement's closing AR; display continuity only. Integer micro-USD (>= 0). */
  openingArUsdMicros: number;
  openingArUsdCents: number;
  openingArUsd: string;
  /** Debt position at the cut = max(0, -(balance at cut)), integer micro-USD (>= 0). */
  closingArUsdMicros: number;
  closingArUsdCents: number;
  closingArUsd: string;
  /** Receipts / prepaid top-ups applied to this statement, integer micro-USD (>= 0). */
  paymentsAppliedUsdMicros: number;
  paymentsAppliedUsdCents: number;
  paymentsAppliedUsd: string;
  /** Closing AR in CNY cents at the issuance-time policy rate; null when unset. */
  cnyReferenceClosingArCents: number | null;
  /** UTC+8 calendar date 'YYYY-MM-DD' = period end + effective grace days. */
  dueDate: string;
  status: BillingStatementStatus;
  createdAt: string;
  closedAt: string | null;
}

export interface V1BillingStatementsResponse {
  statements: readonly V1BillingStatement[];
}
